import * as EUSignCP from 'euscp'
import { KSPs, keyMediaTypes } from './keyTypes'
import { readFile } from './utils'
import { CustomDate } from 'best-modules/utils'
import { ref } from 'vue'
import { caList } from './caList'
import { EndUserSettings, KSPSettings } from 'euscp'
import { loadRememberedKey, saveRememberedKey } from './rememberKey'

const { EndUserConstants, EndUser, EndUserKeyMedia } = EUSignCP

type SignData = {
  signBinary: Uint8Array
  signTimeStamp: string
  signType: EUSignCP.EndUserConstants.EndUserSignType
}

type KeyData = {
  id: string
  privateKeyCtx: EUSignCP.EndUserPrivateKeyContext
  ownerInfo: EUSignCP.EndUserCertificateInfoEx
}

let keys: KeyData[] = []

const pushKey = (
  id: string,
  privateKeyCtx: EUSignCP.EndUserPrivateKeyContext,
  ownerInfo: EUSignCP.EndUserCertificateInfoEx
): KeyData => {
  const key = {
    id,
    privateKeyCtx,
    ownerInfo,
    sign: null,
    signTimeStamp: null,
  }
  keys.push(key)

  return key
}
const removeKey = (id: string): void => {
  keys = keys.filter(k => k.id !== id)
}

const keyMedias = ref<EUSignCP.EndUserKeyMedia[]>([])

const PK_FORM_TYPE_FILE = 'file'
const PK_FORM_TYPE_KM = 'media'
const PK_FORM_TYPE_KSP = 'cloud'

const euSettings: EndUserSettings = {
  language: 'uk',
  encoding: 'utf-8',
  httpProxyServiceURL: '/eu/ProxyHandler.php',
  directAccess: true,
  CAs: '/eu/CAs.json',
  CACertificates: '/eu/CACertificates.p7b',
  allowedKeyMediaTypes: keyMediaTypes,
  // Реєстрація хмарних провайдерів
  KSPs,
}

const euSignFile = new EndUser(
  '\\eu\\euscp.worker.ex-1.3.69.js',
  EndUserConstants.EndUserLibraryType.JS
)

const euSignKeyMedia = new EndUser('', EndUserConstants.EndUserLibraryType.SW)

let euSign = euSignFile

// Типи euscp оголошують context як EndUserContext (не nullable), хоча бібліотека
// приймає null і підставляє контекст за замовчуванням ("If null default one is used").
// Каст прибирає хибну помилку типів TS2345.
const DEFAULT_CTX = null as unknown as EUSignCP.EndUserContext

type InitializeResults = {
  file: null
  media: EUSignCP.EndUserKeyMedia[]
  cloud: KSPSettings[]
}

function initialize<T extends keyof InitializeResults>(
  type: T
): Promise<InitializeResults[T]> {
  return new Promise(function (resolve, reject) {
    let isInitialized = false
    keyMedias.value = []
    console.log('initialize with type', type)

    if ([PK_FORM_TYPE_FILE, PK_FORM_TYPE_KSP].includes(type)) {
      console.log('init')
      euSign = euSignFile
      euSign
        .IsInitialized()
        .then(function (result) {
          isInitialized = result

          if (isInitialized) {
            console.log('EndUser: JS library already initialized')
            return
          }

          console.log('EndUser: JS library initializing...')
          return euSign.Initialize(euSettings)
        })
        .then(function () {
          euSign.SetRuntimeParameter(
            'SignType',
            EndUserConstants.EU_SIGN_TYPE_CADES_X_LONG
          )
          euSign.SetRuntimeParameter('SignIncludeCACertificates', true)
          euSign.SetRuntimeParameter('SignIncludeContentTimeStamp', true)

          if (isInitialized) {
            return
          }

          console.log('EndUser: JS library initialized')

          console.log('EndUser: event listener for KSPs registering...')

          return Promise.resolve()
        })
        .then(function () {
          if (!isInitialized)
            console.log('EndUser: event listener for KSPs registered')

          isInitialized = true
          if (type === PK_FORM_TYPE_KSP) {
            resolve(euSettings.KSPs as InitializeResults[T])
          } else {
            resolve(null as InitializeResults[T])
          }
        })
        .catch(function (e) {
          reject(e)
        })
    }
    if (type === PK_FORM_TYPE_KM) {
      euSign = euSignKeyMedia
      // Перевірка чи встановлені необхідні модулі для роботи криптографічної бібліотеки
      euSign
        .GetLibraryInfo()
        .then(function (
          libInfo: EUSignCP.EndUserLibraryInfoJS | EUSignCP.EndUserLibraryInfoSW
        ) {
          const result = libInfo as EUSignCP.EndUserLibraryInfoSW
          if (!result.supported) {
            throw 'Бібліотека web-підпису не підтримується в вашому браузері або ОС'
          }

          if (!result.loaded) {
            // Бібліотека встановлена, але потребує оновлення
            if (result.isNativeLibraryNeedUpdate) {
              throw `Бібліотека web-підпису потребує оновлення. Будь ласка, встановіть оновлення за посиланням ${result.nativeLibraryInstallURL}`
            }

            // Якщо браузер підтримує web-розширення рекомендується
            // додатково до нативних модулів встановлювати web-розширення
            // Увага! Встановлення web-розширень ОБОВ'ЯЗКОВЕ для ОС Linux та ОС Windows Server
            if (
              result.isWebExtensionSupported &&
              !result.isWebExtensionInstalled
            ) {
              throw (
                'Бібліотека web-підпису потребує встановлення web-розширення. ' +
                'Будь ласка, встановіть web-розширення за посиланням ' +
                result.webExtensionInstallURL +
                ' та оновіть сторінку'
              )
            }

            // Бібліотека (нативні модулі) не встановлені
            throw (
              'Бібліотека web-підпису потребує встановлення. ' +
              'Будь ласка, встановіть бібліотеку за посиланням ' +
              result.nativeLibraryInstallURL +
              ' та оновіть сторінку'
            )
          }

          return euSign.IsInitialized()
        })
        .then(function (result) {
          isInitialized = result
          if (isInitialized) {
            console.log('EndUser: SW library already initialized')
            return
          }

          console.log('EndUser: SW library initializing...')
          return euSign.Initialize(euSettings)
        })
        .then(function () {
          euSign.SetRuntimeParameter(
            'SignType',
            EndUserConstants.EU_SIGN_TYPE_CADES_X_LONG
          )
          euSign.SetRuntimeParameter('SignIncludeCACertificates', true)
          euSign.SetRuntimeParameter('SignIncludeContentTimeStamp', true)

          if (!isInitialized) console.log('EndUser: SW library initialized')

          return euSign.GetKeyMedias()
        })
        .then((kMedias: EUSignCP.EndUserKeyMedia[]) => {
          console.log('kMedias', kMedias)
          keyMedias.value = kMedias
          resolve(kMedias as InitializeResults[T])
        })
        .catch(function (e) {
          reject(e)
        })
    }
  })
}

const saveKey = async () => {
  console.log('saveKey')
  await euSign.SetStorageParameter('test', 'value')
  console.log('SetStorageParameter done')

  const res = await euSign.GetStorageParameter('test')
  console.log('GetStorageParameter done')
  console.log('GetStorageParameter res - ', res)
}

const hashFile = (file: Uint8Array): Promise<string> => {
  return euSign.HashData(
    EndUserConstants.EndUserHashAlgo.GOST34311,
    file,
    true
  ) as Promise<string>
}

async function tryAutoReadPrivateKey(id: string) {
  const saved = loadRememberedKey()
  if (!saved) return null

  let privateKeyCtx

  if (saved.meta?.fileName?.endsWith('.jks')) {
    const jksKeys = await euSign.GetJKSPrivateKeys(saved.keyBytes)
    const pkIndex = 0

    const pkCertificates: Uint8Array[] = []
    for (let i = 0; i < jksKeys[pkIndex].certificates.length; i++)
      pkCertificates.push(jksKeys[pkIndex].certificates[i].data)

    privateKeyCtx = await euSign.CtxReadPrivateKeyBinary(
      DEFAULT_CTX,
      jksKeys[pkIndex].privateKey,
      saved.password,
      pkCertificates,
      saved.meta?.caCN
    )
  } else {
    privateKeyCtx = await euSign.CtxReadPrivateKeyBinary(
      DEFAULT_CTX,
      saved.keyBytes,
      saved.password,
      undefined,
      saved.meta?.caCN
    )
  }

  const certInfoEx = await euSign.CtxGetOwnCertificates(privateKeyCtx)
  console.log('certInfoEx', certInfoEx)
  const key = pushKey(id, privateKeyCtx, certInfoEx[0].infoEx)

  return {
    saved,
    key,
  }
}

function readPrivateKeyFile(
  id: string,
  privateKey: File,
  password: string,
  caAddress: string | null = null,
  rememberKey: boolean = false
): Promise<KeyData> {
  return new Promise((resolve, reject) => {
    if (!privateKey) {
      return reject('Не обрано файл з ос. ключем')
    }
    if (!password) {
      return reject('Не вказано пароль до ос. ключа')
    }

    const caCN: string | undefined =
      !!caAddress && caAddress !== 'auto'
        ? caList.getItem(caAddress)?.issuerCNs[0]
        : undefined
    let pkCertificates: Uint8Array[] | undefined

    // await euSign.ResetPrivateKey()

    readFile(privateKey)
      .then(async function (result) {
        console.log('Private key file readed', result)

        // Якщо файл з ос. ключем має розширення JKS, ключ може містити декілька ключів,
        // для зчитування такого ос. ключа необхіно обрати який ключ повинен зчитуватися
        if (result.file.name.endsWith('.jks')) {
          return euSign
            .GetJKSPrivateKeys(result.data)
            .then(async function (jksKeys) {
              console.log('EndUser: jks keys got')

              // Для спрощення прикладу обирається перший ключ
              const pkIndex = 0

              pkCertificates = []
              for (let i = 0; i < jksKeys[pkIndex].certificates.length; i++)
                pkCertificates.push(jksKeys[pkIndex].certificates[i].data)

              const privateKeyCtx = await euSign.CtxReadPrivateKeyBinary(
                DEFAULT_CTX,
                jksKeys[pkIndex].privateKey,
                password,
                pkCertificates,
                caCN
              )
              return { privateKeyCtx, file: result }
            })
        } else {
          const privateKeyCtx = await euSign.CtxReadPrivateKeyBinary(
            DEFAULT_CTX,
            result.data,
            password,
            pkCertificates,
            caCN
          )

          return { privateKeyCtx, file: result }
        }
      })
      .then(async function (result) {
        const certInfoEx = await euSign.CtxGetOwnCertificates(
          result.privateKeyCtx
        )

        console.log('certInfoEx', certInfoEx)
        const key = pushKey(id, result.privateKeyCtx, certInfoEx[0].infoEx)

        if (rememberKey) {
          saveRememberedKey({
            keyBytes: result.file.data,
            password,
            meta: {
              fileName: result.file.file.name,
              caCN: caAddress ?? undefined,
            },
          })
        }
        resolve(key)
      })
      .catch(function (e) {
        reject(e)
      })
  })
}

function readPrivateKeyMedia(
  id: string,
  keyMedia: EUSignCP.EndUserKeyMedia,
  password: string
): Promise<KeyData> {
  return new Promise((resolve, reject) => {
    if (!keyMedia) {
      return reject('Не обрано файл з ос. ключем')
    }
    if (!password) {
      return reject('Не вказано пароль до ос. ключа')
    }

    return euSign
      .ResetPrivateKey()
      .then(() => {
        const km = new EndUserKeyMedia(keyMedia)
        km.password = password

        return euSign.CtxReadPrivateKey(DEFAULT_CTX, km)
      })
      .then(async function (result) {
        const certInfoEx = await euSign.CtxGetOwnCertificates(result)

        console.log('certInfoEx', certInfoEx)
        const key = pushKey(id, result, certInfoEx[0].infoEx)

        resolve(key)
      })
      .catch(function (e) {
        reject(e)
      })
  })
}

const getSignAlgo = async (
  privateKeyCtx: EUSignCP.EndUserPrivateKeyContext
) => {
  const certificate = await euSign.CtxGetOwnCertificates(privateKeyCtx)
  const { certHashType } = certificate[0].infoEx

  return [
    EndUserConstants.EU_CERT_HASH_TYPE_DSTU7564_256,
    EndUserConstants.EU_CERT_HASH_TYPE_DSTU7564_384,
    EndUserConstants.EU_CERT_HASH_TYPE_DSTU7564_512,
  ].includes(certHashType)
    ? EndUserConstants.EU_CTX_SIGN_DSTU4145_WITH_DSTU7564
    : EndUserConstants.EU_CTX_SIGN_DSTU4145_WITH_GOST34311
}

function signFile(
  privateKeyCtx: EUSignCP.EndUserPrivateKeyContext,
  fileToSign: File | Uint8Array
): Promise<SignData> {
  return new Promise((resolve, reject) => {
    if (!fileToSign) {
      return reject('Не обрано файл для підпису')
    }

    const getFile = async (): Promise<Uint8Array> => {
      return fileToSign instanceof Uint8Array
        ? fileToSign
        : (await readFile(fileToSign)).data
    }

    const getSignBinary = async (
      file: Uint8Array,
      signAlgo: 1 | 4
    ): Promise<Uint8Array> => {
      return (await euSign.CtxSignData(
        privateKeyCtx,
        signAlgo,
        file,
        true,
        true,
        false
      )) as Uint8Array
    }

    const getVerifyData = async (payload: {
      file: Uint8Array
      signBinary: Uint8Array
    }): Promise<EUSignCP.EndUserSignInfo> => {
      return (await euSign.VerifyData(
        payload.file,
        payload.signBinary,
        0
      )) as EUSignCP.EndUserSignInfo
    }

    return getFile()
      .then(async file => {
        const signAlgo = await getSignAlgo(privateKeyCtx)
        const signBinary = await getSignBinary(file, signAlgo)
        const verifyData = await getVerifyData({ file, signBinary })

        resolve({
          signBinary,
          signType:
            verifyData.signLevel as EUSignCP.EndUserConstants.EndUserSignType,
          signTimeStamp: new CustomDate(
            verifyData.timeInfo.signTimeStamp
          ).toString({
            format: 'iso',
            time: true,
          }),
        })
      })
      .catch(e => {
        reject(e?.message || e)
      })
  })
}

export {
  initialize,
  tryAutoReadPrivateKey,
  readPrivateKeyFile,
  readPrivateKeyMedia,
  hashFile,
  signFile,
  keyMedias,
  keys,
  pushKey,
  removeKey,
  saveKey,
}
